import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_ENDPOINTS } from '../../core/config/api.config';
import { HttpService } from '../../core/services/http/http.service';
import { unwrapApiData } from '../../core/utils/api-response.util';
import { environment } from '../../../environments/environment';

export interface CpSubscriptionPlanOption {
  id: string;
  code: string;
  name: string;
  tagline?: string | null;
  description?: string | null;
  userLimit?: number | null;
  projectLimit?: number | null;
  isFree?: boolean;
  pricing: {
    monthly: number;
    quarterly: number | null;
    yearly: number;
  };
}

export interface CpSubscriptionTrialStatus {
  isFreeTrial: boolean;
  trialExpired: boolean;
  requiresUpgrade: boolean;
  daysRemaining: number | null;
  trialEndsAt: string | null;
}

export interface CpSubscriptionAddonCreditBalance {
  addonCode: string;
  name: string;
  unit: string;
  icon: string | null;
  balance: number;
  allocated: number;
  useCost: number;
}

export interface CpSubscriptionUsage {
  activeUsers: number;
  userLimit: number;
  activeProjects: number;
  projectLimit: number;
  usersRemaining: number | null;
  projectsRemaining: number | null;
  isUserUnlimited: boolean;
  isProjectUnlimited: boolean;
}

export interface CpSubscriptionSummary {
  cpId: string;
  trial: CpSubscriptionTrialStatus;
  requiresUpgrade: boolean;
  plan: {
    id: string;
    code: string;
    name: string;
    tagline?: string | null;
    description?: string | null;
    status: string;
    isFree: boolean;
    pricing: CpSubscriptionPlanOption['pricing'];
  } | null;
  subscription: {
    id: string;
    billingCycle: string;
    status: string;
    startsAt?: string | null;
    renewsAt?: string | null;
    daysRemaining: number | null;
  } | null;
  usage: CpSubscriptionUsage;
  addonCredits: CpSubscriptionAddonCreditBalance[];
  entitlements: string[];
  menuEntitlements?: string[];
}

export interface CpSubscriptionAddon {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category?: string | null;
  unit?: string | null;
  icon?: string | null;
  rate: number;
  credits?: number | null;
  validityType?: string | null;
  validityLabel?: string | null;
  dependencyFeatureLabel?: string | null;
  availableBalance?: number;
}

@Injectable({ providedIn: 'root' })
export class CpSubscriptionService {
  private readonly http = inject(HttpService);
  private readonly rawHttp = inject(HttpClient);

  private platformBillingUrl(path: string): string {
    const base = (
      environment.platformBillingApiUrl ?? environment.apiUrl
    ).replace(/\/$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  }

  getSummary(): Observable<CpSubscriptionSummary> {
    return this.http
      .get<unknown>(this.platformBillingUrl(API_ENDPOINTS.CP_SUBSCRIPTION.ME))
      .pipe(map((res) => unwrapApiData<CpSubscriptionSummary>(res)));
  }

  listPlans(): Observable<CpSubscriptionPlanOption[]> {
    return this.http
      .get<unknown>(this.platformBillingUrl(API_ENDPOINTS.CP_SUBSCRIPTION.PLANS))
      .pipe(map((res) => unwrapApiData<CpSubscriptionPlanOption[]>(res) ?? []));
  }

  listAddons(): Observable<CpSubscriptionAddon[]> {
    return this.http
      .get<unknown>(this.platformBillingUrl(API_ENDPOINTS.CP_SUBSCRIPTION.ADDONS))
      .pipe(map((res) => unwrapApiData<CpSubscriptionAddon[]>(res) ?? []));
  }

  listInvoices(): Observable<CpSubscriptionInvoice[]> {
    return this.http
      .get<unknown>(this.platformBillingUrl(API_ENDPOINTS.CP_SUBSCRIPTION.INVOICES))
      .pipe(map((res) => unwrapApiData<CpSubscriptionInvoice[]>(res) ?? []));
  }

  downloadInvoice(invoiceId: string): Observable<Blob> {
    return this.rawHttp.get(
      this.platformBillingUrl(
        API_ENDPOINTS.PLATFORM_BILLING_INVOICE.DOWNLOAD(invoiceId),
      ),
      { responseType: 'blob' },
    );
  }
}

export interface CpSubscriptionInvoice {
  id: string;
  invoiceNumber: string;
  status: string;
  totalAmountInr: number;
  invoiceDate: string;
  dueDate?: string | null;
}
